/**
 * Record a category choice in the server ledger.
 *
 * Best effort and fire-and-forget: a logging failure must never affect the
 * visitor. Nothing extra about the person is collected to store the choice —
 * only the anonymous ID they already have, the purpose, the timestamp and
 * the notice version.
 *
 * The advertising category is ALSO written by the existing marketing-consent
 * path (MetaBootstrap → recordMarketingConsent), which keeps Consent Mode v2
 * and Meta eligibility in step. This writer adds the analytics category and
 * the notice version for both, so the audit trail is complete.
 */
import { OPTIONAL_CATEGORIES } from "./categories";
import type { PrivacyConsentState } from "./store";

export async function logCategoryChoice(state: PrivacyConsentState): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const { getAnonymousId } = await import("@/lib/identity/anonymousId");
    const anonymousId = getAnonymousId();
    if (!anonymousId || anonymousId.length < 8) return;

    const channels = OPTIONAL_CATEGORIES.filter((c) => state[c] !== "unknown").map((c) => ({
      channel: c as "analytics" | "advertising",
      status: state[c] as "granted" | "denied",
    }));
    if (channels.length === 0) return;

    const { recordConsentChannels } = await import("@/lib/consent-log.functions");
    await recordConsentChannels({
      data: {
        anonymousId,
        source: state.source.slice(0, 40),
        policyVersion: state.noticeVersion,
        explicit: true,
        channels,
        pageUrl: window.location.origin,
        userAgent: navigator.userAgent.slice(0, 400),
      },
    });
  } catch {
    /* never break the page for a consent log */
  }
}
